import React, { useMemo, useRef } from "react";
import { Keyboard } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  loginRequest,
  changeEmail,
  changePassword,
} from "@microdoing/shared/services/login/actions";
import sanitizeName from "@microdoing/shared/utils/sanitizeName";
import i18n from "@microdoing/shared/i18n";
import {
  RoundInput,
  MainTitle,
  MainText,
  Box,
  MainButton,
  PressableOpacity,
} from "app/components/common";
import { useNavigation } from "@react-navigation/native";
import {
  getLoginEmail,
  getLoginError,
  getLoginPassword,
  getLoginRequesting,
} from "@microdoing/shared/services/login/selectors";
import HeaderScreen from "app/components/common/HeaderScreen";
import KeyboardAvoidWrapper from "app/components/common/KeyboardAvoidWrapper";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import normalize from "app/utils/normalize";
import WebLoginScreen from "./WebLoginScreen";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const LoginScreen = () => {
  const email = useSelector(getLoginEmail);
  const password = useSelector(getLoginPassword);
  const error = useSelector(getLoginError);
  const requesting = useSelector(getLoginRequesting);
  const refPasswordInput = useRef();
  const insets = useSafeAreaInsets();

  const isButtonDisable = useMemo(() => {
    return email === "" || password === "";
  }, [email, password]);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const onChangeEmail = (text: string) => {
    dispatch(changeEmail(text));
  };

  const onChangePassword = (text: string) => {
    dispatch(changePassword(text));
  };

  const onSubmit = () => {
    if (email !== "" && password !== "") {
      dispatch(
        loginRequest({
          email: sanitizeName(email),
          password: password,
        }),
      );
    }
    Keyboard.dismiss();
  };

  const onRegister = () => {
    navigation.navigate("Signup");
  };

  const onForgetPassword = () => {
    navigation.navigate("ForgotPassword");
  };

  const renderBody = () => {
    return (
      <>
        {isWebDesktop && (
          <MainText variant="l4-medium" marginBottom="xs">
            {i18n.t("web-signup-email")}
          </MainText>
        )}
        <RoundInput
          containerPropStyle={styles.input}
          testID="login_email_input"
          name="email"
          textContentType="emailAddress"
          autoCapitalize="none"
          onChangeText={onChangeEmail}
          placeholder="E-mail"
          value={email}
          keyboardType="email-address"
          autoFocus
          returnKeyType="done"
          onSubmitEditing={() => refPasswordInput?.current?.focus()}
        />
        {isWebDesktop && (
          <MainText variant="l4-medium" marginBottom="xs" marginTop="l">
            {i18n.t("web-signup-password")}
          </MainText>
        )}
        <RoundInput
          refInput={refPasswordInput}
          textContentType="oneTimeCode"
          containerPropStyle={styles.input}
          testID="login_password_input"
          name="password"
          autoCapitalize="none"
          onChangeText={onChangePassword}
          placeholder={i18n.t("password")}
          value={password}
          secureTextEntry={true}
          returnKeyType="done"
          onSubmitEditing={onSubmit}
        />
        <Box alignItems="flex-end" marginTop="xs" marginBottom="xs">
          <PressableOpacity onPress={onForgetPassword}>
            <Box
              flexDirection="row"
              justifyContent="flex-end"
              alignItems="center"
            >
              <MainText variant="l4-semibold" color="brand.500">
                {i18n.t("password-forgotten")}
              </MainText>
            </Box>
          </PressableOpacity>
        </Box>
        {error !== "" ? (
          <MainText variant="l3-medium" color="system.red.950">
            {error}
          </MainText>
        ) : null}
      </>
    );
  };

  if (isWebDesktop) {
    return (
      <WebLoginScreen
        body={renderBody()}
        isButtonDisable={isButtonDisable}
        onRegister={onRegister}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <Box
      variant="screenView"
      backgroundColor="neutral.white"
      style={{ marginTop: insets.top }}
    >
      <HeaderScreen title="" leftIcon />
      <KeyboardAvoidWrapper>
        <Box>
          <MainTitle variant="l8-bold">{i18n.t("welcome-back")}</MainTitle>
          <MainText variant="l5-medium" color="neutral.500" marginTop="xs">
            {i18n.t("welcome-subtitle")}
          </MainText>
          <Box marginTop="xm" marginBottom="s">
            {renderBody()}
          </Box>
          <MainButton
            isLoading={requesting}
            isDisabled={isButtonDisable}
            text={i18n.t("loginButton")}
            onPress={onSubmit}
            testID="login_button_submit"
          />
          <Box alignItems="center">
            <PressableOpacity onPress={onRegister}>
              <Box flexDirection="row" justifyContent="center" marginTop="xs">
                <MainText variant="l4-medium" color="neutral.800">
                  {i18n.t("create-new-account")}
                </MainText>
                <MainText variant="l4-semibold" color="brand.500">
                  {i18n.t("register")}
                </MainText>
              </Box>
            </PressableOpacity>
          </Box>
        </Box>
      </KeyboardAvoidWrapper>
    </Box>
  );
};

const styles = {
  input: {
    borderRadius: isWebDesktop ? normalize(8) : normalize(4),
    marginBottom: isWebDesktop ? 0 : normalize(20),
    width: "100%",
    height: 57,
  },
  register: {
    marginTop: normalize(20),
  },
};

export default LoginScreen;
